import { Can, useAbility } from '@/lib/casl'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { BillingBooleanSettingField } from '@/components/customers/BillingBooleanSettingField'
import {
  HouseAccountSettingsSection,
  getPaymentTermsDisabledTooltip,
  isHouseAccountEnabled,
  setHouseAccountEnabled,
} from '@/components/customers/HouseAccountSettingsSection'
import { useAppForm } from '@/lib/form'
import {
  useCheckDuplicateCustomerPhoneMutation,
  useCreateCustomerMutation,
  useSoftDeleteCustomerMutation,
  useUpdateCustomerMutation,
} from '@/lib/services/customers/customers.query'
import {
  cleanCustomerAddressDraft,
  customerAddressDraftFrom,
} from '@/lib/services/customers/customers.address'
import { customerSchema } from '@/lib/services/customers/customers.schema'
import dayjs from '@/lib/dayjs'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  BadgePercent,
  Building2,
  HandCoins,
  AlertTriangle,
  ExternalLink,
  MapPin,
  NotebookPen,
  Plus,
  ReceiptText,
  Trash2,
  Truck,
  User,
} from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { usePathHelper } from '@/hooks/usePathHelper'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Textarea } from '../ui/textarea'
import { Typography } from '@/components/ui/typography'
import SaveBar from './SaveBar'
import DeleteButton from './DeleteButton'
import { CustomerSourceType } from '@/lib/enums/CustomerSourceType'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { CustomerAddressFieldInputs } from './CustomerAddressFields'

function normalizeCustomerBillingDefaults(defaults = {}) {
  const billingDefaults = defaults ?? {}
  const payLaterAllowed = billingDefaults.pay_later_allowed !== false
  const allowFulfillmentBeforePayment =
    payLaterAllowed && billingDefaults.allow_fulfillment_before_payment === true
  const paymentTermsCode =
    billingDefaults.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value

  return {
    pay_later_allowed: payLaterAllowed,
    allow_fulfillment_before_payment: allowFulfillmentBeforePayment,
    credit_limit:
      payLaterAllowed &&
      billingDefaults.credit_limit !== null &&
      typeof billingDefaults.credit_limit !== 'undefined'
        ? String(billingDefaults.credit_limit)
        : '',
    payment_terms_code: allowFulfillmentBeforePayment
      ? paymentTermsCode
      : CustomerPaymentTerms.DueOnReceipt.Value,
    payment_terms_days:
      allowFulfillmentBeforePayment && paymentTermsCode === CustomerPaymentTerms.Custom.Value
        ? String(billingDefaults.payment_terms_days ?? '')
        : '',
  }
}

function getEmptyCustomer(billingDefaults = {}) {
  const normalizedBillingDefaults = normalizeCustomerBillingDefaults(billingDefaults)

  return {
    name: '',
    email: '',
    phone: '',
    customer_type: 'individual',
    is_business: false,
    pay_later_allowed: normalizedBillingDefaults.pay_later_allowed,
    allow_fulfillment_before_payment: normalizedBillingDefaults.allow_fulfillment_before_payment,
    billing_email: '',
    statement_delivery_method: 'automatic',
    credit_limit: normalizedBillingDefaults.credit_limit,
    payment_terms_code: normalizedBillingDefaults.payment_terms_code,
    payment_terms_days: normalizedBillingDefaults.payment_terms_days,
    tax_exempt: false,
    physical_address: customerAddressDraftFrom(null),
    billing_address: customerAddressDraftFrom(null),
    physical_address_is_billing: true,
    internal_notes: [],
  }
}

function transformCustomerForForm(customer, billingDefaults = {}) {
  if (!customer) {
    return getEmptyCustomer(billingDefaults)
  }

  const payLaterAllowed = customer.pay_later_allowed !== false
  const allowFulfillmentBeforePayment =
    payLaterAllowed && Boolean(customer.allow_fulfillment_before_payment)

  return {
    name: customer.name || '',
    email: customer.email || '',
    phone: customer.phone || '',
    customer_type: customer.is_business ? 'business' : 'individual',
    is_business: Boolean(customer.is_business),
    pay_later_allowed: payLaterAllowed,
    allow_fulfillment_before_payment: allowFulfillmentBeforePayment,
    billing_email: customer.billing_email || '',
    statement_delivery_method: customer.statement_delivery_method || 'automatic',
    credit_limit: customer.credit_limit ?? '',
    payment_terms_code: customer.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value,
    payment_terms_days:
      customer.payment_terms_code === CustomerPaymentTerms.Custom.Value
        ? String(customer.payment_terms_days ?? '')
        : '',
    tax_exempt: Boolean(customer.tax_exempt),
    physical_address: customerAddressDraftFrom(customer.physical_address),
    billing_address: customerAddressDraftFrom(customer.billing_address),
    physical_address_is_billing: Boolean(customer.physical_address_is_billing),
    internal_notes: Array.isArray(customer.internal_notes) ? customer.internal_notes : [],
  }
}

function cleanCustomerPayload(
  value,
  { canManageCustomerBilling = true, canEditCustomerNotes = true } = {}
) {
  const {
    customer_type,
    pay_later_allowed: _payLaterAllowed,
    allow_fulfillment_before_payment: _allowFulfillmentBeforePayment,
    billing_email: _billingEmail,
    statement_delivery_method: _statementDeliveryMethod,
    credit_limit: _creditLimit,
    payment_terms_code: _paymentTermsCode,
    payment_terms_days: _paymentTermsDays,
    tax_exempt: _taxExempt,
    internal_notes: _internalNotes,
    ...payload
  } = value
  const creditLimit = String(value.credit_limit ?? '').trim()
  const paymentTermsCode = value.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value
  const paymentTermsDays = String(value.payment_terms_days ?? '').trim()
  const payLaterAllowed = value.pay_later_allowed !== false
  const allowFulfillmentBeforePayment =
    payLaterAllowed && value.allow_fulfillment_before_payment === true

  const customerPayload = {
    ...payload,
    is_business: customer_type ? customer_type === 'business' : Boolean(value.is_business),
    physical_address: cleanCustomerAddressDraft(value.physical_address),
    billing_address: value.physical_address_is_billing
      ? null
      : cleanCustomerAddressDraft(value.billing_address),
    physical_address_is_billing: Boolean(value.physical_address_is_billing),
    ...(canEditCustomerNotes ? { internal_notes: value.internal_notes || [] } : {}),
  }

  if (!canManageCustomerBilling) {
    return customerPayload
  }

  return {
    ...customerPayload,
    pay_later_allowed: payLaterAllowed,
    allow_fulfillment_before_payment: allowFulfillmentBeforePayment,
    billing_email: value.billing_email?.trim() || null,
    statement_delivery_method: value.statement_delivery_method || 'automatic',
    credit_limit: payLaterAllowed && creditLimit !== '' ? creditLimit : null,
    payment_terms_code: allowFulfillmentBeforePayment
      ? paymentTermsCode
      : CustomerPaymentTerms.DueOnReceipt.Value,
    payment_terms_days:
      allowFulfillmentBeforePayment && paymentTermsCode === CustomerPaymentTerms.Custom.Value
        ? Math.max(0, Number.parseInt(paymentTermsDays, 10) || 0)
        : null,
    tax_exempt: Boolean(value.tax_exempt),
  }
}

function getPaymentTermsOptions() {
  return Object.values(CustomerPaymentTerms).map((term) => ({
    id: term.Value,
    name: term.Name,
  }))
}

function phoneDigits(value) {
  return String(value ?? '').replace(/\D/g, '')
}

function phoneIdentityDigits(value) {
  const digits = phoneDigits(value)

  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
}

function duplicateCustomerDisplayName(customer) {
  return customer?.name || customer?.email || customer?.phone || `Customer #${customer?.id}`
}

function CustomerDuplicatePhoneDialog({
  customer,
  isPending,
  onCancel,
  onConfirm,
  open,
  otherCustomerHref,
}) {
  const displayName = duplicateCustomerDisplayName(customer)

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? onCancel() : undefined)}>
      <DialogContent size="small">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <AlertTriangle className="size-5 text-status-warning-foreground" />
            Phone Number Already Used
          </DialogTitle>
          <DialogDescription>
            Another customer already uses this phone number. You can still save this customer, but
            shoppers who share this number may need to sign in with email instead of text.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="font-medium text-foreground">{displayName}</div>
          {customer?.email ? <div className="text-muted-foreground">{customer.email}</div> : null}
          {customer?.phone ? (
            <div className="text-muted-foreground">{formatPhone(customer.phone)}</div>
          ) : null}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {otherCustomerHref ? (
            <Button type="button" variant="ghost" asChild>
              <a href={otherCustomerHref} target="_blank" rel="noreferrer">
                <ExternalLink className="size-4" />
                Open Other Customer
              </a>
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={onConfirm} disabled={isPending}>
              {isPending ? 'Saving...' : 'Save Anyway'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AddressFields({ form, id, prefix, title, hiddenMessage = null, children = null }) {
  return (
    <Card id={`${id}-${prefix}`}>
      <CardHeader>
        <CardTitle className="inline-flex items-center">
          <MapPin className="mr-2 size-5 inline" />
          <Typography variant="h4">{title}</Typography>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {children}
        {hiddenMessage ? (
          <Typography
            variant="supporting"
            className="block rounded-lg border border-border/60 bg-muted/25 p-3"
          >
            {hiddenMessage}
          </Typography>
        ) : (
          <CustomerAddressFieldInputs form={form} id={id} prefix={prefix} />
        )}
      </CardContent>
    </Card>
  )
}

function CustomerNotesEditor({ form }) {
  const notes = useStore(form.store, (state) => state.values.internal_notes || [])
  const [draft, setDraft] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)

  const resetDraft = () => {
    setDraft('')
    setEditingIndex(null)
  }

  const saveNote = () => {
    const note = draft.trim()
    if (!note) {
      return
    }

    let nextNotes = [...notes]
    if (editingIndex === null) {
      nextNotes = [{ note, timestamp: new Date().toISOString() }, ...notes]
    } else {
      nextNotes[editingIndex] = {
        ...nextNotes[editingIndex],
        note,
      }
    }

    form.setFieldValue('internal_notes', nextNotes)
    resetDraft()
  }

  const deleteNote = (index) => {
    form.setFieldValue(
      'internal_notes',
      notes.filter((_, noteIndex) => noteIndex !== index)
    )

    if (editingIndex === index) {
      resetDraft()
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center">
          <NotebookPen className="mr-2 size-5 inline" />
          <Typography variant="h4">Internal Notes</Typography>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Add a private note for the shop team..."
            rows={3}
          />
          <div className="flex justify-end gap-2">
            {editingIndex !== null ? (
              <Button type="button" variant="outline" onClick={resetDraft}>
                Cancel
              </Button>
            ) : null}
            <Button type="button" onClick={saveNote} disabled={!draft.trim()}>
              <Plus className="size-4" />
              {editingIndex === null ? 'Add Note' : 'Save Note'}
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {notes.length === 0 ? (
            <Typography variant="supporting">No internal notes yet.</Typography>
          ) : (
            notes.map((note, index) => (
              <div
                key={`${note.timestamp}-${index}`}
                className="rounded-lg border bg-muted/20 p-3 text-sm"
              >
                <p className="whitespace-pre-wrap">{note.note}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <Typography variant="supporting">
                    {note.timestamp ? dayjs(note.timestamp).format('MMM D, YYYY h:mm A') : ''}
                  </Typography>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDraft(note.note)
                        setEditingIndex(index)
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Delete note"
                      onClick={() => deleteNote(index)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const CustomerForm = ({ initialData, billingDefaults = null }) => {
  const ability = useAbility()
  const path = usePathHelper()
  const id = useId()
  const navigate = useNavigate()
  const { orgId } = useParams({ strict: false })

  const createCustomerMutation = useCreateCustomerMutation()
  const updateCustomerMutation = useUpdateCustomerMutation()
  const checkDuplicatePhoneMutation = useCheckDuplicateCustomerPhoneMutation()
  const deleteCustomerMutation = useSoftDeleteCustomerMutation({ cleanupOnSuccess: false })
  const isEditing = !!initialData?.id
  const canManageCustomerBilling = ability.can('manage', 'customers', 'billing')
  const canEditCustomerNotes = ability.can('edit', 'customers', 'notes')
  const confirmedDuplicatePhoneRef = useRef(null)
  const phoneBaselineDigitsRef = useRef(phoneIdentityDigits(initialData?.phone))
  const [duplicatePhoneWarning, setDuplicatePhoneWarning] = useState(null)
  const [houseAccountRevocationRequest, setHouseAccountRevocationRequest] = useState(null)

  const shouldCheckDuplicatePhone = (customerPayload) => {
    const nextPhoneDigits = phoneIdentityDigits(customerPayload.phone)

    if (!nextPhoneDigits) {
      return false
    }

    if (!isEditing) {
      return true
    }

    return nextPhoneDigits !== phoneBaselineDigitsRef.current
  }

  const saveCustomer = async ({ customerPayload, data, formApi, verb }) => {
    const mutation = isEditing ? updateCustomerMutation : createCustomerMutation
    const response = await mutation.mutateAsync(data)

    toast.success(`Customer ${verb}d successfully`)
    const responseCustomer = response && typeof response === 'object' ? response : null
    const savedCustomer =
      responseCustomer ?? (isEditing ? { ...initialData, ...customerPayload } : null)

    if (savedCustomer) {
      phoneBaselineDigitsRef.current = phoneIdentityDigits(savedCustomer.phone)
      requestAnimationFrame(() => formApi.reset(transformCustomerForForm(savedCustomer)))
    }

    const createdCustomerId = response?.customer_id ?? response?.id
    if (createdCustomerId && !isEditing) {
      return navigate({
        ...path.customers.show(createdCustomerId).link,
        ignoreBlocker: true,
      })
    }

    return undefined
  }

  const form = useAppForm({
    defaultValues: transformCustomerForForm(initialData, billingDefaults),
    validators: {
      onChange: customerSchema,
      onSubmit: customerSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        const customerPayload = cleanCustomerPayload(value, {
          canManageCustomerBilling,
          canEditCustomerNotes,
        })
        const data = isEditing
          ? { customerId: initialData.id, ...customerPayload }
          : { ...customerPayload, source_type: CustomerSourceType.Admin.Value }
        const verb = isEditing ? 'update' : 'create'
        const nextPhoneDigits = phoneIdentityDigits(customerPayload.phone)

        try {
          if (
            shouldCheckDuplicatePhone(customerPayload) &&
            confirmedDuplicatePhoneRef.current !== nextPhoneDigits
          ) {
            const duplicateResponse = await checkDuplicatePhoneMutation.mutateAsync({
              phone: customerPayload.phone,
              excludeCustomerId: isEditing ? initialData.id : null,
            })
            const duplicateCustomer = duplicateResponse?.duplicate_customer ?? null

            if (duplicateCustomer?.id) {
              setDuplicatePhoneWarning({
                customer: duplicateCustomer,
                phoneDigits: nextPhoneDigits,
              })

              return undefined
            }
          }

          const result = await saveCustomer({ customerPayload, data, formApi, verb })
          confirmedDuplicatePhoneRef.current = null

          return result
        } catch (error) {
          if (
            isEditing &&
            error?.response?.status === 409 &&
            error?.response?.data?.code === 'house_account_revocation_confirmation_required'
          ) {
            setHouseAccountRevocationRequest({ customerPayload, data, formApi, verb })
            return undefined
          }

          toast.error(`Failed to ${verb} customer`, {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const duplicateCustomerHref = duplicatePhoneWarning?.customer?.id
    ? (path.customers.show(duplicatePhoneWarning.customer.id).href ??
      path.customers.show(duplicatePhoneWarning.customer.id).link.to)
    : null

  const handleConfirmDuplicatePhoneSave = () => {
    confirmedDuplicatePhoneRef.current = duplicatePhoneWarning?.phoneDigits ?? null
    setDuplicatePhoneWarning(null)
    requestAnimationFrame(() => {
      void form.handleSubmit()
    })
  }

  const handleCancelDuplicatePhoneSave = () => {
    setDuplicatePhoneWarning(null)
  }

  const handleConfirmHouseAccountRevocation = async () => {
    if (!houseAccountRevocationRequest) return

    try {
      await saveCustomer({
        ...houseAccountRevocationRequest,
        data: {
          ...houseAccountRevocationRequest.data,
          confirm_house_account_revocation: true,
        },
      })
      confirmedDuplicatePhoneRef.current = null
      setHouseAccountRevocationRequest(null)
    } catch (error) {
      toast.error('Failed to update customer', {
        description: getErrorMessage(error),
      })
      throw error
    }
  }

  const physicalAddressIsBilling = useStore(
    form.store,
    (state) => state.values.physical_address_is_billing
  )
  const paymentTermsCode = useStore(form.store, (state) => state.values.payment_terms_code)
  const payLaterAllowed = useStore(form.store, (state) => state.values.pay_later_allowed !== false)
  const allowFulfillmentBeforePayment = useStore(
    form.store,
    (state) => state.values.allow_fulfillment_before_payment === true
  )
  const houseAccountEnabled = isHouseAccountEnabled(payLaterAllowed, allowFulfillmentBeforePayment)
  const billingSettingsLink = path.billing?.index?.().using?.({ orgId })?.link

  return (
    <div className="max-w-4xl">
      <form.AppForm id={id} aria-label="Customer Form">
        <div className="space-y-4">
          <form.FormError />
          <form.Form>
            <div className="grid gap-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="inline-flex items-center">
                    <User className="mr-2 inline" />
                    <Typography variant="h4">Customer Information</Typography>
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 md:col-span-6">
                      <form.AppField name="name">
                        {(field) => <field.TextInput label="Name" placeholder="Customer Name" />}
                      </form.AppField>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <form.AppField name="phone">
                        {(field) => <field.PhoneInput label="Phone" />}
                      </form.AppField>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <form.AppField name="email">
                        {(field) => <field.EmailInput label="Email" />}
                      </form.AppField>
                    </div>
                    <div className="col-span-6">
                      <form.AppField name="customer_type">
                        {(field) => (
                          <field.RadioButtonGroup
                            label="Customer Type"
                            options={[
                              {
                                id: 'individual',
                                name: 'Individual',
                                start: <User className="size-4" />,
                              },
                              {
                                id: 'business',
                                name: 'Business',
                                start: <Building2 className="size-4" />,
                              },
                            ]}
                            classNames={{
                              container: 'w-full sm:w-auto',
                              button: 'h-10 min-w-32 gap-2',
                            }}
                          />
                        )}
                      </form.AppField>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <AddressFields
                form={form}
                id={id}
                prefix="physical_address"
                title="Physical Address"
              />

              <AddressFields
                form={form}
                id={id}
                prefix="billing_address"
                title="Billing Address"
                hiddenMessage={
                  physicalAddressIsBilling
                    ? 'Billing address will use the physical address for this customer.'
                    : null
                }
              >
                <div className="rounded-lg border border-border/60 bg-muted/25 p-3">
                  <form.AppField name="physical_address_is_billing">
                    {(field) => (
                      <field.SwitchInput
                        label="Use physical address as billing address"
                        description="Turn this off to enter a different billing address."
                      />
                    )}
                  </form.AppField>
                </div>
              </AddressFields>

              {canManageCustomerBilling ? (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <CardTitle className="inline-flex items-center">
                        <ReceiptText className="mr-2 size-5 inline" />
                        <Typography variant="h4">Billing Setup</Typography>
                      </CardTitle>
                      {!isEditing && billingSettingsLink ? (
                        <Can do="list" on="billing-settings">
                          <Link
                            {...billingSettingsLink}
                            className="text-sm text-muted-foreground underline-offset-4 transition hover:text-foreground hover:underline"
                          >
                            Change default billing settings
                          </Link>
                        </Can>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent className="grid gap-5">
                    <HouseAccountSettingsSection
                      enabled={houseAccountEnabled}
                      onEnabledChange={(enabled) => setHouseAccountEnabled(form, enabled)}
                    >
                      <form.AppField name="pay_later_allowed">
                        {(field) => (
                          <BillingBooleanSettingField
                            field={field}
                            icon={HandCoins}
                            title="Allow Pay Later Orders"
                            description="Let this customer place orders before the full balance is collected."
                          />
                        )}
                      </form.AppField>

                      {!payLaterAllowed ? (
                        <Typography variant="supporting" wrap="pretty" className="block">
                          Turn Pay Later on before setting fulfillment-before-payment, terms, or a
                          credit limit.
                        </Typography>
                      ) : null}

                      <form.AppField name="allow_fulfillment_before_payment">
                        {(field) => (
                          <BillingBooleanSettingField
                            field={field}
                            icon={Truck}
                            title="Allow Fulfillment Before Payment"
                            description="Let orders be picked up or delivered before payment is collected."
                            disabled={!payLaterAllowed}
                            displayValue={payLaterAllowed && allowFulfillmentBeforePayment}
                          />
                        )}
                      </form.AppField>
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 md:col-span-3">
                          <form.AppField name="credit_limit">
                            {(field) => (
                              <field.MoneyInput
                                label="Credit Limit"
                                placeholder="No limit"
                                showFreeBadge={false}
                                formatEmptyAsZero={false}
                                disabled={!payLaterAllowed}
                              />
                            )}
                          </form.AppField>
                        </div>
                        <div className="col-span-6 md:col-span-3">
                          <form.AppField name="payment_terms_code">
                            {(field) => (
                              <field.SelectInput
                                label="Payment Terms"
                                options={getPaymentTermsOptions()}
                                disabled={!payLaterAllowed || !allowFulfillmentBeforePayment}
                                disabledTooltip={getPaymentTermsDisabledTooltip(
                                  payLaterAllowed,
                                  allowFulfillmentBeforePayment
                                )}
                              />
                            )}
                          </form.AppField>
                        </div>
                        {paymentTermsCode === CustomerPaymentTerms.Custom.Value ? (
                          <div className="col-span-6 md:col-span-3">
                            <form.AppField name="payment_terms_days">
                              {(field) => (
                                <field.TextInput
                                  label="Custom Days"
                                  placeholder="30"
                                  inputMode="numeric"
                                  disabled={!payLaterAllowed || !allowFulfillmentBeforePayment}
                                />
                              )}
                            </form.AppField>
                          </div>
                        ) : null}
                      </div>
                    </HouseAccountSettingsSection>

                    <form.AppField name="tax_exempt">
                      {(field) => (
                        <BillingBooleanSettingField
                          field={field}
                          icon={BadgePercent}
                          title="Tax Exempt"
                          description="Mark this customer as exempt from sales tax."
                        />
                      )}
                    </form.AppField>

                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 md:col-span-3">
                        <form.AppField name="statement_delivery_method">
                          {(field) => (
                            <field.SelectInput
                              label="Statement delivery"
                              options={[
                                { name: 'Automatic (email when available)', id: 'automatic' },
                                { name: 'Email', id: 'email' },
                                { name: 'Print', id: 'print' },
                              ]}
                            />
                          )}
                        </form.AppField>
                        <form.AppField name="billing_email">
                          {(field) => (
                            <field.EmailInput
                              label="Billing Email"
                              placeholder="billing@example.com"
                            />
                          )}
                        </form.AppField>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {isEditing && canEditCustomerNotes ? <CustomerNotesEditor form={form} /> : null}
            </div>
            <Can do={isEditing ? 'edit' : 'create'} on="customers">
              <SaveBar submitButtonText={isEditing ? 'Update' : 'Save'} />
            </Can>
          </form.Form>
        </div>
      </form.AppForm>
      {isEditing && (
        <Can do="delete" on="customers">
          <DeleteButton
            id={initialData.id}
            path={path.customers.index()}
            model="Customer"
            mutation={deleteCustomerMutation}
            replace
            onDeleted={deleteCustomerMutation.cleanupDeletedCustomer}
          />
        </Can>
      )}
      <CustomerDuplicatePhoneDialog
        customer={duplicatePhoneWarning?.customer ?? null}
        isPending={createCustomerMutation.isPending || updateCustomerMutation.isPending}
        onCancel={handleCancelDuplicatePhoneSave}
        onConfirm={handleConfirmDuplicatePhoneSave}
        open={Boolean(duplicatePhoneWarning)}
        otherCustomerHref={duplicateCustomerHref}
      />
      <ConfirmationDialog
        trigger={null}
        open={Boolean(houseAccountRevocationRequest)}
        onOpenChange={(open) => {
          if (!open) setHouseAccountRevocationRequest(null)
        }}
        title="Turn Off House Account?"
        description="Orders with a balance will immediately require payment before they can move forward through fulfillment, including orders already in progress."
        confirmText="Turn Off Anyway"
        pendingText="Updating..."
        isPending={updateCustomerMutation.isPending}
        closeOnError={false}
        onConfirm={handleConfirmHouseAccountRevocation}
      />
    </div>
  )
}

export default CustomerForm
